import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense } from "react";
import { CookieBanner } from "@/components/CookieBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";

// Route-level code splitting: each page is a separate JS chunk.
// The browser only downloads a page's bundle when the user first navigates to it.
const Home           = lazy(() => import("@/pages/Home"));
const About          = lazy(() => import("@/pages/About"));
const Features       = lazy(() => import("@/pages/Features"));
const Pricing        = lazy(() => import("@/pages/Pricing"));
const CheckoutSuccess= lazy(() => import("@/pages/CheckoutSuccess"));
const AppPage        = lazy(() => import("@/pages/AppPage"));
const Contact        = lazy(() => import("@/pages/Contact"));
const Terms          = lazy(() => import("@/pages/Terms"));
const Privacy        = lazy(() => import("@/pages/Privacy"));
const SalesSheet     = lazy(() => import("@/pages/SalesSheet"));
const ResetPassword  = lazy(() => import("@/pages/ResetPassword"));
const Profile        = lazy(() => import("@/pages/Profile"));
const Listings       = lazy(() => import("@/pages/Listings"));
const NotFound       = lazy(() => import("@/pages/not-found"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't re-fetch on window focus for static/slow-changing data
      refetchOnWindowFocus: false,
      // Re-fetch automatically when the browser comes back online
      refetchOnReconnect: true,
      // Retry up to 3 times with exponential backoff
      retry: 3,
      retryDelay: (attempt) => Math.min(500 * Math.pow(2, attempt), 30_000),
      // Keep successful results for 5 minutes; cache for 30 minutes
      staleTime: 5 * 60_000,
      gcTime:    30 * 60_000,
    },
    mutations: {
      retry: 1,
      retryDelay: 1_000,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#1B3D8F] border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground font-medium">Loading…</span>
      </div>
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/features" component={Features} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/checkout/success" component={CheckoutSuccess} />
        <Route path="/app" component={AppPage} />
        <Route path="/contact" component={Contact} />
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/sales-sheet" component={SalesSheet} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/profile" component={Profile} />
        <Route path="/listings" component={Listings} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            {/* Offline/online indicator — renders nothing during normal browsing */}
            <OfflineBanner />
            <Router />
          </WouterRouter>
          <Toaster />
          <CookieBanner />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
